import { getCartErrorMessage } from '@theme/cart-feedback';

class SProductForm extends HTMLElement {
  #ac;
  #abortController;
  #activeSubmitter;
  #activeSubmitterAriaDisabled;
  #activeSubmitterHadOpacityClass;
  #activeSubmitterHadCursorWaitClass;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('submit', (event) => {
      if (!this.#shouldHandleSubmit(event)) return;

      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      this.#submit(event.target, event.submitter);
    }, opts);

    // CMoon box extras: remember ticks/colours so a box colour change (partial re-render) keeps them.
    this.addEventListener('change', (event) => {
      const row = event.target.closest?.('[data-box-extra]');
      if (row) this.#rememberExtra(row);
    }, opts);
    this.#extrasObserver = new MutationObserver(() => this.#restoreExtras());
    this.#extrasObserver.observe(this, { childList: true, subtree: true });
  }

  #extraState = new Map();
  #extrasObserver;

  #rememberExtra(row) {
    const toggle = row.querySelector('[data-box-extra-toggle]');
    const select = row.querySelector('[data-box-extra-variant]');
    this.#extraState.set(row.dataset.extraKey, { checked: Boolean(toggle?.checked), variantId: select?.value || '' });
    this.#syncExtraImage(row);
  }

  #restoreExtras() {
    if (!this.#extraState.size) return;
    for (const row of this.querySelectorAll('[data-box-extra]')) {
      const state = this.#extraState.get(row.dataset.extraKey);
      if (!state || row.dataset.restored) continue;
      row.dataset.restored = 'true';
      const toggle = row.querySelector('[data-box-extra-toggle]');
      const select = row.querySelector('[data-box-extra-variant]');
      if (select && state.variantId && [...select.options].some((option) => option.value === state.variantId)) {
        select.value = state.variantId;
      }
      if (toggle) toggle.checked = state.checked;
      this.#syncExtraImage(row);
    }
  }

  #syncExtraImage(row) {
    const option = row.querySelector('[data-box-extra-variant]')?.selectedOptions?.[0];
    const image = row.querySelector('[data-box-extra-image]');
    if (option?.dataset.image && image) image.src = option.dataset.image;
  }

  #extraLines(form, boxLine) {
    const group = form.querySelector('[data-box-extras]');
    if (!group) return [];
    const extraKey = group.dataset.extraProperty || 'Da inserire nella box';
    const boxKey = group.dataset.boxProperty || 'Extra nella box';
    const boxLabel = group.dataset.boxLabel || '';
    const lines = [];
    const names = [];
    for (const row of group.querySelectorAll('[data-box-extra]')) {
      if (!row.querySelector('[data-box-extra-toggle]')?.checked) continue;
      const option = row.querySelector('[data-box-extra-variant]')?.selectedOptions?.[0];
      const variantId = option?.value || row.dataset.variantId;
      if (!variantId) continue;
      const variantTitle = option?.dataset.title || row.querySelector('[data-title]')?.dataset.title || '';
      names.push(variantTitle ? `${row.dataset.extraTitle} – ${variantTitle}` : row.dataset.extraTitle);
      lines.push({ merchandiseId: variantId, quantity: 1, attributes: [{ key: extraKey, value: boxLabel }] });
    }
    if (names.length) {
      boxLine.attributes = [...(boxLine.attributes || []), { key: boxKey, value: names.join(', ') }];
    }
    return lines;
  }

  #clearExtras() {
    this.#extraState.clear();
    for (const toggle of this.querySelectorAll('[data-box-extra-toggle]')) toggle.checked = false;
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#extrasObserver?.disconnect();
    // Cart refresh replaces suggestion forms during a successful add. Let that request
    // finish instead of cancelling the refresh that removed this element.
    this.#setSubmitting(false);
  }

  #shouldHandleSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return false;

    // If Standard Actions are unavailable, leave the Shopify product form native.
    if (!window.Shopify?.actions?.updateCart) return false;

    if (!this.#variantInput(form)) return false;

    const submitter = event.submitter;
    if (submitter instanceof HTMLElement && !submitter.matches('button[name="add"], input[name="add"]')) {
      return false;
    }

    return true;
  }

  async #submit(form, submitter) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    // CMoon: on product pages, add the variant the customer actually picked (checked option),
    // even if the hidden variant field has not been refreshed yet.
    const pickedVariantId = this.#pickedVariantId(form);
    const variantField = this.#variantInput(form);
    if (pickedVariantId && variantField) variantField.value = pickedVariantId;

    const formData = new FormData(form);
    // Refreshed suggestion cards carry the exact displayed variant on their submitter.
    // Do not trust a retained hidden input's live value after DOM reconciliation.
    if (submitter?.dataset.addVariantId) {
      formData.set('id', submitter.dataset.addVariantId);
    }
    const line = this.#lineFromFormData(formData);
    if (!line) {
      this.#abortController = null;
      return;
    }

    const extraLines = this.#extraLines(form, line);

    this.#setSubmitting(true, submitter);
    this.#showError('');

    try {
      const result = await window.Shopify.actions.updateCart(
        { lines: [line, ...extraLines] },
        {
          event: {
            context: 'product',
            detail: this.#eventDetail(),
          },
          signal,
        }
      );

      this.#throwIfAborted(signal);

      const errorMessage = this.#resultErrorMessage(result);
      if (errorMessage) {
        this.#showError(errorMessage);
        // CMoon: card buttons are small, so also surface the reason (e.g. last piece already in the cart) as a toast.
        if (this.hasAttribute('data-stay-on-page')) {
          document.dispatchEvent(new CustomEvent('s-notification:add', {
            detail: { message: errorMessage, variant: 'error', duration: 5000 },
          }));
        }
      } else {
        if (extraLines.length) this.#clearExtras();
        // CMoon: keep the shopper on the page; confirm with a toast (header count refreshes via cart partials).
        const addedMessage = this.dataset.addedMessage
          || document.querySelector('[data-added-message]')?.dataset.addedMessage
          || '';
        document.dispatchEvent(new CustomEvent('s-notification:add', {
          detail: { message: addedMessage, variant: 'brand', duration: 2500 },
        }));
      }
    } catch (error) {
      if (signal.aborted) return;

      this.#showError(getCartErrorMessage(error) || this.dataset.errorMessage || '');
    } finally {
      if (!signal.aborted) {
        this.#setSubmitting(false);
        if (this.#abortController?.signal === signal) this.#abortController = null;
      }
    }
  }

  #pickedVariantId(form) {
    const picker = form.querySelector('s-variant-picker');
    if (!picker) return '';
    // With one option, the checked value maps to exactly one variant; multi-option products keep the hidden field.
    if (picker.querySelectorAll('fieldset').length !== 1) return '';
    const radio = picker.querySelector('input[type="radio"][data-variant-id]:checked');
    if (radio) return radio.dataset.variantId || '';
    const select = picker.querySelector('select');
    return select?.selectedOptions?.[0]?.dataset?.variantId || '';
  }

  #variantInput(form) {
    const input = form.elements.namedItem('id');
    return input instanceof HTMLInputElement ? input : null;
  }

  #lineFromFormData(formData) {
    const variantId = String(formData.get('id') || '').trim();
    if (!variantId) return null;

    const quantityValue = Number.parseInt(String(formData.get('quantity') || '1'), 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const line = {
      merchandiseId: variantId,
      quantity,
    };

    const sellingPlanId = String(formData.get('selling_plan') || '').trim();
    if (sellingPlanId) line.sellingPlanId = sellingPlanId;

    const attributes = this.#lineAttributes(formData);
    if (attributes.length) line.attributes = attributes;

    return line;
  }

  #lineAttributes(formData) {
    const attributes = [];

    for (const [name, value] of formData) {
      const match = String(name).match(/^properties\[(.+)]$/);
      if (!match) continue;
      if (typeof File !== 'undefined' && value instanceof File) continue;

      const attributeValue = String(value).trim();
      if (!attributeValue) continue;

      attributes.push({ key: match[1], value: attributeValue });
    }

    return attributes;
  }

  #eventDetail() {
    const detail = { source: 's-product-form' };
    if (this.dataset.productId) detail.productId = this.dataset.productId;
    return detail;
  }

  #resultErrorMessage(result) {
    return result?.userErrors?.[0]?.message || '';
  }

  #showError(message) {
    const error = this.querySelector('[data-add-to-cart-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }

  #setSubmitting(submitting, submitter = this.#activeSubmitter) {
    if (submitting) {
      this.setAttribute('aria-busy', 'true');
      if (submitter instanceof HTMLElement) {
        this.#activeSubmitter = submitter;
        this.#activeSubmitterAriaDisabled = submitter.getAttribute('aria-disabled');
        this.#activeSubmitterHadOpacityClass = submitter.classList.contains('opacity-50');
        this.#activeSubmitterHadCursorWaitClass = submitter.classList.contains('cursor-wait');
        submitter.setAttribute('aria-disabled', 'true');
        submitter.classList.add('opacity-50', 'cursor-wait');
      }
      return;
    }

    this.removeAttribute('aria-busy');
    if (this.#activeSubmitter instanceof HTMLElement) {
      if (this.#activeSubmitterAriaDisabled == null) {
        this.#activeSubmitter.removeAttribute('aria-disabled');
      } else {
        this.#activeSubmitter.setAttribute('aria-disabled', this.#activeSubmitterAriaDisabled);
      }
      if (!this.#activeSubmitterHadOpacityClass) this.#activeSubmitter.classList.remove('opacity-50');
      if (!this.#activeSubmitterHadCursorWaitClass) this.#activeSubmitter.classList.remove('cursor-wait');
    }
    this.#activeSubmitter = null;
    this.#activeSubmitterAriaDisabled = null;
    this.#activeSubmitterHadOpacityClass = null;
    this.#activeSubmitterHadCursorWaitClass = null;
  }

  #throwIfAborted(signal) {
    if (!signal.aborted) return;

    if (typeof signal.throwIfAborted === 'function') {
      signal.throwIfAborted();
    }

    throw new DOMException('Aborted', 'AbortError');
  }
}

if (!customElements.get('s-product-form')) {
  customElements.define('s-product-form', SProductForm);
}
